import { useMe } from '@smarthome/api';
import { ROUTES } from '@smarthome/ui';
import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate, Outlet, RouterProvider } from 'react-router-dom';

import { ToastProvider } from './components/ui';
import { NotificationProvider, NotificationsPage } from './features/notifications';
import { RootLayout } from './layouts/RootLayout';
import {
  // Dashboard pages
  DashboardsPage,
  DashboardPage,
  DashboardEditPage,
  // Device pages
  DevicesPage,
  VirtualDevicesPage,
  // Home page
  HomePage,
  // Login page
  LoginPage,
  // Scene pages
  ScenesPage,
  SceneDetailPage,
  ScenesReorderPage,
  SceneActivationHistoryPage,
  // Sensor pages
  SensorsPage,
  SensorDetailPage,
  SensorsBulkEditPage,
  // Settings pages
  SettingsPage,
  ApiKeysPage,
  ChannelsSettingsPage,
  HeartbeatsSettingsPage,
  HomeLocationSettingsPage,
  InputsSettingsPage,
  LogsPage,
  NotificationsSettingsPage,
  RegionalSettingsPage,
  ServerInfoPage,
  UnitTypesPage,
  // Integration settings
  EosSettingsPage,
  DirigeraSettingsPage,
  HueSettingsPage,
  HttpSettingsPage,
  MatterSettingsPage,
  MqttSettingsPage,
  MqttMonitorPage,
  ShellySettingsPage,
  SlideSettingsPage,
  WebosSettingsPage,
  // Utility pages
  UtilitiesPage,
  UtilitiesManagePage,
  UtilityCategoryPage,
  // Other pages
  NotFoundPage,
} from './pages';

// Lazy load design system (dev only) - will be tree-shaken in production
const DesignSystemPage = import.meta.env.DEV
  ? lazy(() => import('./pages/design-system').then((m) => ({ default: m.DesignSystemPage })))
  : () => null;

const router = createBrowserRouter([
  // Public routes
  { path: ROUTES.LOGIN, element: <LoginPage /> },

  // Protected routes
  {
    element: <ProtectedRoute />,
    children: [
      {
        element: <RootLayout />,
        errorElement: <RootLayout showError />,
        children: [
          // Home
          { path: ROUTES.HOME, element: <HomePage /> },
          { path: ROUTES.NOTIFICATIONS, element: <NotificationsPage /> },

          // Dashboards
          {
            path: 'dashboards',
            children: [
              { index: true, element: <DashboardsPage /> },
              { path: ':id', element: <DashboardPage /> },
              { path: ':id/edit', element: <DashboardEditPage /> },
            ],
          },

          // Devices
          {
            path: 'devices',
            children: [
              { index: true, element: <DevicesPage /> },
              { path: 'virtual', element: <VirtualDevicesPage /> },
            ],
          },

          // Scenes
          {
            path: 'scenes',
            children: [
              { index: true, element: <ScenesPage /> },
              { path: 'new', element: <SceneDetailPage /> },
              { path: 'reorder', element: <ScenesReorderPage /> },
              { path: ':id', element: <SceneDetailPage /> },
              { path: ':id/history', element: <SceneActivationHistoryPage /> },
            ],
          },

          // Sensors
          {
            path: 'sensors',
            children: [
              { index: true, element: <SensorsPage /> },
              { path: 'bulk-edit', element: <SensorsBulkEditPage /> },
              { path: ':id', element: <SensorDetailPage /> },
            ],
          },

          // Utilities
          {
            path: 'utilities',
            children: [
              { index: true, element: <UtilitiesPage /> },
              { path: 'manage', element: <UtilitiesManagePage /> },
              { path: ':categoryId', element: <UtilityCategoryPage /> },
            ],
          },

          // Settings
          {
            path: 'settings',
            children: [
              { index: true, element: <SettingsPage /> },
              { path: 'regional', element: <RegionalSettingsPage /> },
              { path: 'home-location', element: <HomeLocationSettingsPage /> },
              { path: 'heartbeats', element: <HeartbeatsSettingsPage /> },
              { path: 'inputs', element: <InputsSettingsPage /> },
              { path: 'notifications', element: <NotificationsSettingsPage /> },
              { path: 'channels', element: <ChannelsSettingsPage /> },
              { path: 'server-info', element: <ServerInfoPage /> },
              { path: 'api-keys', element: <ApiKeysPage /> },
              { path: 'logs', element: <LogsPage /> },
              { path: 'unit-types', element: <UnitTypesPage /> },
              // Integration settings
              {
                path: 'integrations',
                children: [
                  { path: 'eos', element: <EosSettingsPage /> },
                  { path: 'ikea-dirigera', element: <DirigeraSettingsPage /> },
                  { path: 'philips-hue', element: <HueSettingsPage /> },
                  { path: 'http', element: <HttpSettingsPage /> },
                  { path: 'matter', element: <MatterSettingsPage /> },
                  {
                    path: 'mqtt',
                    children: [
                      { index: true, element: <MqttSettingsPage /> },
                      { path: 'monitor', element: <MqttMonitorPage /> },
                    ],
                  },
                  { path: 'shelly', element: <ShellySettingsPage /> },
                  { path: 'slide', element: <SlideSettingsPage /> },
                  { path: 'webos', element: <WebosSettingsPage /> },
                ],
              },
            ],
          },

          // Design System - Development Only
          ...(import.meta.env.DEV
            ? [
                {
                  path: ROUTES.DESIGN_SYSTEM,
                  element: (
                    <Suspense fallback={<DesignSystemLoading />}>
                      <DesignSystemPage />
                    </Suspense>
                  ),
                },
              ]
            : []),

          // 404
          { path: '*', element: <NotFoundPage /> },
        ],
      },
    ],
  },
]);

export function App() {
  return (
    <ToastProvider>
      <RouterProvider router={router} />
    </ToastProvider>
  );
}

function DesignSystemLoading() {
  return (
    <div className="flex min-h-[400px] items-center justify-center">
      <div className="h-6 w-6 animate-spin rounded-full border-2 border-zinc-700 border-t-blue-500" />
    </div>
  );
}

function ProtectedRoute() {
  const { isLoading, isError } = useMe();

  if (isLoading) {
    return <AuthLoading />;
  }

  if (isError) {
    return <Navigate to={ROUTES.LOGIN} replace />;
  }

  return (
    <NotificationProvider>
      <Outlet />
    </NotificationProvider>
  );
}

function AuthLoading() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 dark:bg-zinc-950">
      <div className="h-8 w-8 animate-spin rounded-full border-2 border-gray-300 border-t-blue-500 dark:border-zinc-700" />
    </div>
  );
}
